import axiosInstance from "@/utility/axiosInstance";
import { trackGoogleEvent } from "@/mixins/eventTracking";

export default {
  getReferralMessage({ commit }, code) {
    commit("SHOW_LOADING_PANEL");

    return axiosInstance
      .get("/referral", {
        params: {
          referral_code: code,
        },
      })
      .then((response) => {
        commit("SET_REFERRAL_STATE", {
          isValid: true,
          code,
          message: response.data.message,
          showMessage: true,
        });

        // save referral code in local storage
        localStorage.setItem("referralCode", code);

        // track events
        trackGoogleEvent(
          "Customer clicks through to STB via a valid referral link",
          "Campaign & Channel",
          "Valid referral code",
          { referral_code: code },
        );
      })
      .catch(() => {
        commit("SET_REFERRAL_STATE", {
          isValid: false,
          code: "",
          message: "Referral Code Invalid",
          showMessage: true,
        });

        trackGoogleEvent(
          "Customer clicks through to STB via a invalid referral link",
          "Campaign & Channel",
          "Invalid referral code",
          { referral_code: code },
        );
      })
      .then(() => {
        commit("HIDE_LOADING_PANEL");
      });
  },
  getAffiliateMessage({ commit }, code) {
    commit("SHOW_LOADING_PANEL");

    return axiosInstance
      .get("/affiliate", {
        params: {
          affiliate_code: code,
        },
      })
      .then((response) => {
        commit("SET_AFFILIATE_STATE", {
          isValid: true,
          code,
          message: response.data.message,
          showMessage: true,
        });

        // save referral code in local storage
        localStorage.setItem("affiliateCode", code);

        trackGoogleEvent(
          "Customer clicks through to STB via a valid affiliate link",
          "Campaign & Channel",
          "Valid affiliate code",
          { affiliate_code: code },
        );
      })
      .catch(() => {
        commit("SET_AFFILIATE_STATE", {
          isValid: false,
          code: "",
          message: "Affiliate Code Invalid",
          showMessage: false,
        });

        trackGoogleEvent(
          "Customer clicks through to STB via a invalid affiliate link",
          "Campaign & Channel",
          "Invalid affiliate code",
          { affiliate_code: code },
        );
      })
      .then(() => {
        commit("HIDE_LOADING_PANEL");
      });
  },
  createPreSignupLead({ state, commit }, { agreedToPolicy, discountCode }) {
    const leadData = {
      firstName: state.personalDetails.firstname,
      lastName: state.personalDetails.lastname,
      emailAddress: state.personalDetails.email,
      mobileNumber: state.personalDetails.tel,
      agreedToPolicy,
      discountCode,
      leadType: state.personalDetails.leadType,
      whatsappConsent: state.personalDetails.whatsappConsent,
    };

    if (state.leadId) {
      leadData.leadId = state.leadId;
    }

    if (state.affiliate.isValid) {
      leadData.affiliateCode = state.affiliate.code;
    }

    return axiosInstance.post("/pre-signup", leadData).then(({ data }) => {
      commit("SET_LEAD_ID", data);
    });
  },
  updatePreSignupLeadProductData(
    { state, getters },
    { offerCode, affiliateCode, referralCode },
  ) {
    if (!state.leadId) {
      return;
    }

    return axiosInstance.post(`/pre-signup/${state.leadId}/products`, {
      offerCode,
      affiliateCode,
      referralCode,
      priceIds: getters.selectedProductPriceIds,
      contentInsuranceVariation: getters.contentInsuranceVariation,
    });
  },
  getProducts(
    { state, getters },
    {
      addressGuid,
      postcode,
      housemateCount,
      forceRefreshAddress,
      voucherCode,
      lettingAgentName,
      hash,
    },
  ) {
    let leadUpdatePromise;

    if (state.leadId) {
      leadUpdatePromise = axiosInstance.post(
        `/pre-signup/${state.leadId}/household`,
        {
          groupSize: housemateCount,
          addressGuid,
          postcode,
          lettingAgentName,
          contentInsuranceVariation: getters.contentInsuranceVariation,
        },
      );
    } else {
      leadUpdatePromise = Promise.resolve(null);
    }

    const getProductsPromise = axiosInstance.get("/products", {
      params: {
        addressGuid,
        postcode,
        hm8s: housemateCount,
        fra: forceRefreshAddress,
        voucherCode,
        hash,
      },
    });

    return Promise.all([getProductsPromise, leadUpdatePromise]).then(
      ([productResponse]) => productResponse,
    );
  },
  getAccountInformation(context, code) {
    return axiosInstance.get("/summary", {
      params: {
        guid: code,
      },
    });
  },
  getUIConfiguration({ commit }) {
    return axiosInstance.get("/config").then(({ data }) => {
      commit("SET_UI_STATE_PROP", data);
    });
  },
  activateDiscount(
    _,
    { voucherCode, selectedProducts, housemateCount, leadId },
  ) {
    return axiosInstance.post(`/voucher/${voucherCode}`, {
      selectedProducts,
      housemateCount,
      leadId,
    });
  },
  setValidationErrors({ commit }, errors) {
    commit("SET_VALIDATION_ERRORS", errors);
  },
  clearValidationErrors({ commit }) {
    commit("CLEAR_VALIDATION_ERRORS");
  },
  clearSession({ commit }) {
    commit("RESET_STATE");
  },
  async fetchFeatureFlags({ state, commit }) {
    if (
      (Array.isArray(state.featureFlags) && state.featureFlags.length > 0) ||
      state.featureFlagsChecked
    ) {
      return;
    }
    const response = await axiosInstance.get("/features");
    const flags = Array.isArray(response.data) ? response.data : [];
    commit("SET_FEATURE_FLAGS", flags);

    // Set Google Analytics user type from feature flag
    const analyticsUserTypeFlag = flags.find(
      (f) => f.key === "analytics_user_type",
    );
    if (analyticsUserTypeFlag !== undefined && analyticsUserTypeFlag.value) {
      const { setGAUserType } = require("@/mixins/eventTracking");
      // LaunchDarkly returns 'internal' or 'external' string value
      setGAUserType(analyticsUserTypeFlag.value);
    }
  },
  async getFeatureFlagByKey({ state, dispatch }, key) {
    if (
      (!Array.isArray(state.featureFlags) || state.featureFlags.length === 0) &&
      !state.featureFlagsChecked
    ) {
      await dispatch("fetchFeatureFlags");
    }
    const flag = state.featureFlags[key];
    return flag ? flag.value : undefined;
  },
};
